/**
 * Export-Funktionen für Admin-Dashboard
 * Unterstützt Excel, CSV und JSON Export
 */

import type { AuditResult, CompanyProfile } from './index';

/**
 * Konvertiert Daten zu CSV
 */
export function exportToCSV(data: any[], filename: string) {
  if (data.length === 0) {
    alert('Keine Daten zum Exportieren vorhanden.');
    return;
  }
  
  // Header aus erstem Objekt extrahieren
  const headers = Object.keys(data[0]);
  
  // CSV-Zeilen erstellen
  const csvRows = [
    headers.join(','), // Header-Zeile
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape Kommas und Anführungszeichen
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ];
  
  const csvContent = csvRows.join('\n');
  downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
}

/**
 * Konvertiert Daten zu JSON
 */
export function exportToJSON(data: any, filename: string) {
  const jsonContent = JSON.stringify(data, null, 2);
  downloadFile(jsonContent, filename, 'application/json;charset=utf-8;');
}

/**
 * Exportiert Audit-Submissions als CSV
 */
export function exportSubmissionsToCSV(submissions: any[]) {
  const flatData = submissions.map(sub => ({
    'Timestamp': new Date(sub.timestamp).toLocaleString('de-DE'),
    'Unternehmen': sub.companyProfile.companyName,
    'Branche': sub.companyProfile.industry,
    'Teamgröße': sub.companyProfile.teamSize,
    'CRM': sub.companyProfile.hasCRM ? 'Ja' : 'Nein',
    'Marketing Automation': sub.companyProfile.hasMarketingAutomation ? 'Ja' : 'Nein',
    'Monatlicher Umsatzverlust': sub.result?.computedMetrics?.monthlyRevenueGap || 'N/A',
    'Jährlicher Umsatzverlust': sub.result?.computedMetrics?.annualRevenueGap || 'N/A'
  }));

  exportToCSV(flatData, `audit-submissions-${Date.now()}.csv`);
}

/**
 * Exportiert Funnel-Daten als CSV
 */
export function exportFunnelDataToCSV(submissions: any[]) {
  const funnelData = submissions
    .filter(sub => sub.companyProfile.funnelData)
    .map(sub => {
      const fd = sub.companyProfile.funnelData;
      return {
        'Unternehmen': sub.companyProfile.companyName,
        'Monatlicher Umsatz': fd.monthlyRevenue || 'N/A',
        'Kontakte/Monat': fd.monthlyContacts || 'N/A',
        'Erstgespräche/Monat': fd.monthlyDiscoveryCalls || 'N/A',
        'Präsentationen/Monat': fd.monthlyPresentations || 'N/A',
        'Abschlüsse/Monat': fd.monthlyDeals || 'N/A',
        'Ø Deal-Größe': fd.averageDealSize || 'N/A',
        'Sales Cycle (Monate)': fd.salesCycleMonths || 'N/A',
        'Reaktionszeit': fd.responseTime || 'N/A',
        'Follow-up-Disziplin': fd.followUpDiscipline || 'N/A',
        'Lead-Qualität': fd.leadQuality || 'N/A',
        'Pipeline-Transparenz': fd.pipelineTransparency || 'N/A'
      };
    });
  
  if (funnelData.length === 0) {
    alert('Keine Funnel-Daten zum Exportieren vorhanden.');
    return;
  }
  
  exportToCSV(funnelData, `funnel-data-${Date.now()}.csv`);
}

/**
 * Exportiert Kategorie-Scores als CSV
 */
export function exportCategoryScoresToCSV(submissions: any[]) {
  const categoryData = submissions.flatMap(sub => 
    sub.result.categoryScores.map((cs: any) => ({
      'Unternehmen': sub.companyProfile.companyName,
      'Kategorie': cs.categoryId,
      'Score': `${cs.percentage}%`,
      'Erreichte Punkte': cs.score,
      'Maximale Punkte': cs.maxScore
    }))
  );
  
  exportToCSV(categoryData, `category-scores-${Date.now()}.csv`);
}

/**
 * Exportiert komplette Audit-Daten als JSON
 */
export function exportFullAuditDataToJSON(submissions: any[]) {
  const exportData = {
    exportDate: new Date().toISOString(),
    totalSubmissions: submissions.length,
    submissions: submissions.map(sub => ({
      timestamp: sub.timestamp,
      companyProfile: sub.companyProfile,
      result: sub.result,
      answers: sub.answers,
      revenueCalculation: sub.revenueCalculation
    }))
  };
  
  exportToJSON(exportData, `full-audit-data-${Date.now()}.json`);
}

/**
 * Exportiert Analytics-Daten als JSON
 */
export function exportAnalyticsToJSON(submissions: any[]) {
  // Berechne Analytics
  const industries = submissions.reduce((acc: any, sub) => {
    const industry = sub.companyProfile.industry;
    if (!acc[industry]) {
      acc[industry] = { count: 0, avgScore: 0, scores: [] };
    }
    acc[industry].count++;
    acc[industry].scores.push(sub.result.percentage);
    return acc;
  }, {});
  
  // Durchschnitte berechnen
  Object.keys(industries).forEach(industry => {
    const scores = industries[industry].scores;
    industries[industry].avgScore = scores.reduce((a: number, b: number) => a + b, 0) / scores.length;
    industries[industry].median = scores.sort((a: number, b: number) => a - b)[Math.floor(scores.length / 2)];
    delete industries[industry].scores; // Entferne rohe Scores
  });
  
  const analyticsData = {
    exportDate: new Date().toISOString(),
    totalAudits: submissions.length,
    avgScore: submissions.reduce((sum, sub) => sum + sub.result.percentage, 0) / submissions.length,
    industries,
    maturityDistribution: submissions.reduce((acc: any, sub) => {
      const level = sub.result.maturityLevel;
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    }, {}),
    crmAdoption: {
      withCRM: submissions.filter(s => s.companyProfile.hasCRM).length,
      withoutCRM: submissions.filter(s => !s.companyProfile.hasCRM).length
    }
  };
  
  exportToJSON(analyticsData, `analytics-${Date.now()}.json`);
}

/**
 * Helper: Download-Datei
 */
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
